import React from 'react';
import { ArrowLeft, ShieldCheck, AlertTriangle, Flag, Phone, Mail } from 'lucide-react';

const Section = ({ id, icon: Icon, iconColor, title, children }) => (
  <section className="mb-10" aria-labelledby={id}>
    <div className="flex items-center gap-3 mb-4">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: iconColor === 'primary' ? 'rgba(74,173,168,0.12)' : 'rgba(169,156,200,0.12)' }}
      >
        <Icon className={`w-4 h-4 ${iconColor === 'primary' ? 'text-primary' : 'text-secondary'}`} aria-hidden="true" />
      </div>
      <h2 id={id} className="font-heading text-lg font-semibold text-primary">{title}</h2>
    </div>
    {children}
  </section>
);

const Para = ({ children }) => (
  <p className="text-text-main text-sm leading-relaxed mb-3">{children}</p>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2.5 text-text-main text-sm leading-relaxed">
    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" aria-hidden="true" />
    <span>{children}</span>
  </li>
);

const Divider = () => (
  <div
    className="h-px mb-8"
    style={{ background: 'linear-gradient(to right, transparent, #4AADA820, #A99CC830, #4AADA820, transparent)' }}
    aria-hidden="true"
  />
);

const ChildSafetyPage = ({ onNavigate }) => (
  <div className="min-h-screen bg-background">
    {/* Brand gradient strip */}
    <div className="h-1 w-full" style={{ background: 'linear-gradient(to right, #4AADA8, #A99CC8)' }} aria-hidden="true" />

    <div className="max-w-2xl mx-auto px-6 py-12">
      {/* Back button */}
      {onNavigate && (
        <button
          onClick={() => onNavigate('feed')}
          aria-label="Back to Sirati"
          className="flex items-center gap-2 text-sm text-text-muted hover:text-primary transition-colors mb-10 min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          Back to Sirati
        </button>
      )}

      {/* Header */}
      <header className="text-center mb-12">
        <p className="font-heading text-3xl font-bold tracking-wide text-primary mb-1">SIRATI</p>
        <h1 className="font-heading text-2xl font-semibold mb-2" style={{ color: '#A99CC8' }}>Child Safety Policy</h1>
        <p className="text-xs text-text-muted">Last updated: May 2026</p>
      </header>

      <Divider />

      <Para>
        Sirati is a platform for Muslim adults seeking personal growth and marriage readiness. The safety of children is a fundamental commitment we take seriously. This page explains our standards, how we enforce them, and how to report concerns.
      </Para>

      {/* Minimum age */}
      <Section id="minimum-age" icon={ShieldCheck} iconColor="primary" title="Minimum Age Requirement">
        <Para>
          Sirati is strictly for users aged <strong>18 and over</strong>. By creating an account, users confirm they are at least 18 years old. We do not knowingly allow minors to register or use the platform.
        </Para>
        <Para>
          If we become aware that a user is under 18, we will immediately suspend their account and delete their personal data in accordance with applicable law.
        </Para>
      </Section>

      <Divider />

      {/* CSAE prohibition */}
      <Section id="prohibited-content" icon={AlertTriangle} iconColor="secondary" title="Prohibition of Child Sexual Abuse and Exploitation Content">
        <Para>
          Sirati has a zero-tolerance policy for child sexual abuse and exploitation (CSAE) content of any kind. This includes, but is not limited to:
        </Para>
        <ul className="space-y-1.5 ml-1 mb-4">
          {[
            'Child sexual abuse material (CSAM) in any format',
            'Grooming, solicitation, or exploitation of minors',
            'Any content that sexualizes individuals under the age of 18',
            'Sharing, linking to, or promoting CSAE material on or off the platform',
          ].map((item, i) => <Bullet key={i}>{item}</Bullet>)}
        </ul>
        <Para>
          Any account found to have posted, shared, or engaged in CSAE-related activity will be permanently banned immediately. We will also preserve relevant evidence and report the account to the appropriate authorities.
        </Para>
      </Section>

      <Divider />

      {/* Reporting */}
      <Section id="reporting" icon={Flag} iconColor="primary" title="Reporting Harmful Content">
        <Para>
          Every user can report content or behavior they believe is harmful, including any content involving minors. Reports are reviewed by our moderation team.
        </Para>
        <Para>
          <strong>To report in the app:</strong> tap the three-dot menu (⋯) on any post or visit a user's profile and use the Report option. Select the reason that best describes the concern — for content involving minors, select "Child safety" or the closest available option.
        </Para>
        <Para>
          <strong>To report by email:</strong> if you are unable to report in-app, or if you need to escalate an urgent concern, contact us directly at{' '}
          <a href="mailto:salam@sirati.co?subject=Child%20Safety%20Report" className="text-primary hover:underline">
            salam@sirati.co
          </a>{' '}
          with the subject line <em>Child Safety Report</em>. Include as much detail as possible — usernames, post content, and any screenshots.
        </Para>
      </Section>

      <Divider />

      {/* Legal compliance */}
      <Section id="legal-compliance" icon={Phone} iconColor="secondary" title="Legal Compliance and Reporting to Authorities">
        <Para>
          Sirati complies with all applicable laws regarding child sexual abuse material, including the PROTECT Our Children Act and any obligations under local law.
        </Para>
        <Para>
          If we discover CSAM on the platform, we are legally required to — and will — report it to:
        </Para>
        <ul className="space-y-1.5 ml-1 mb-4">
          {[
            'The National Center for Missing and Exploited Children (NCMEC) CyberTipline in the United States',
            'Local law enforcement and relevant regulatory authorities in the applicable jurisdiction',
          ].map((item, i) => <Bullet key={i}>{item}</Bullet>)}
        </ul>
        <Para>
          We cooperate fully with law enforcement investigations involving child safety.
        </Para>
      </Section>

      <Divider />

      {/* Contact */}
      <Section id="contact" icon={Mail} iconColor="primary" title="Contact Us">
        <Para>
          If you have questions about this policy, want to report a child safety concern, or need to escalate a matter that was not resolved through the in-app reporting system, please contact us:
        </Para>
        <div
          className="my-5 p-4 rounded-arch-lg border"
          style={{ borderColor: '#B8E0E0', background: 'rgba(74,173,168,0.05)' }}
        >
          <a
            href="mailto:salam@sirati.co?subject=Child%20Safety"
            className="font-heading font-semibold text-primary hover:underline text-lg"
          >
            salam@sirati.co
          </a>
          <p className="text-xs text-text-muted mt-1">Subject: Child Safety</p>
        </div>
        <Para>
          We treat every child safety report as urgent and will respond as quickly as possible.
        </Para>
      </Section>
    </div>
  </div>
);

export default ChildSafetyPage;
